import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { MapPinIcon, CheckCircleIcon, TruckIcon, ShieldIcon, ZapIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
interface LocationPageProps {
  setPage: (page: string) => void;
}
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
export function LocationPageTemplate({
  setPage
}: LocationPageProps) {
  // Sample data for Detroit, MI
  const locationData = {
    city: 'Detroit',
    region: 'Metro Detroit',
    description: 'Providing professional, mobile dustless and dry blasting services directly to your site in Detroit and the surrounding metro area. From historic brick restoration to heavy industrial equipment cleaning, we bring the right equipment to get the job done fast and safely.'
  };
  const localServices = ['Historic Brick & Concrete Restoration', 'Industrial Machinery Blasting', 'Automotive & Classic Car Stripping', 'Graffiti Removal for Commercial Properties', 'Fleet Vehicle Surface Prep', 'Marine & Boat Bottom Cleaning'];
  return <div className="w-full bg-white" data-id="element-428">
      {/* Hero Section */}
      <section className="bg-charcoal pt-20 pb-16 px-4 relative overflow-hidden" data-id="element-429">
        <div className="absolute inset-0 z-0 opacity-20" data-id="element-430">
          <img src="https://images.unsplash.com/photo-1580659369931-1e9672051052?q=80&w=1920&auto=format&fit=crop" alt="Detroit skyline" className="w-full h-full object-cover" data-id="element-431" />
          <div className="absolute inset-0 bg-charcoal/80 mix-blend-multiply" data-id="element-432"></div>
        </div>
        <div className="max-w-7xl mx-auto text-center relative z-10" data-id="element-433">
          <motion.div initial="hidden" animate="visible" variants={fadeInUp} className="inline-flex items-center space-x-2 bg-steel-900/80 border border-steel-700 px-4 py-2 rounded-full mb-6" data-id="element-434">
            <MapPinIcon className="w-4 h-4 text-brand-red" data-id="element-435" />
            <span className="text-steel-200 text-sm font-heading font-bold uppercase tracking-wider" data-id="element-436">
              Serving {locationData.region}
            </span>
          </motion.div>
          <motion.h1 initial="hidden" animate="visible" variants={fadeInUp} className="text-4xl md:text-6xl font-heading font-extrabold text-white uppercase tracking-tight mb-6" data-id="element-437">
            Mobile Dustless Blasting in{' '}
            <span className="text-brand-red" data-id="element-438">{locationData.city}, MI</span>
          </motion.h1>
          <motion.p initial="hidden" animate="visible" variants={fadeInUp} className="text-lg md:text-xl text-steel-300 font-body max-w-3xl mx-auto leading-relaxed mb-8" data-id="element-439">
            {locationData.description}
          </motion.p>
          <motion.div initial="hidden" animate="visible" variants={fadeInUp} className="flex flex-col sm:flex-row justify-center items-center gap-4" data-id="element-440">
            <Button variant="primary" size="lg" onClick={() => setPage('contact')} showPhoneIcon data-id="element-441">
              Call (517) 777-8088
            </Button>
            <Button variant="outline" size="lg" onClick={() => setPage('contact')} data-id="element-442">
              Get a Free Local Quote
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Local Services Section */}
      <section className="py-20 bg-white" data-id="element-443">
        <div className="max-w-7xl mx-auto px-4" data-id="element-444">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-445">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={staggerContainer} data-id="element-446">
              <motion.h2 variants={fadeInUp} className="text-3xl md:text-5xl font-heading font-extrabold uppercase text-charcoal mb-6" data-id="element-447">
                Surface Prep Services in{' '}
                <span className="text-brand-red" data-id="element-448">{locationData.city}</span>
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-steel-600 mb-8 leading-relaxed" data-id="element-449">
                As a fully mobile operation, Merem Blasting brings
                industrial-grade surface preparation directly to your facility,
                job site, or driveway in {locationData.region}. We handle
                everything from delicate historic restorations to aggressive
                industrial coating removal.
              </motion.p>
              <div className="space-y-4 mb-8" data-id="element-450">
                {localServices.map((service, idx) => <motion.div key={idx} variants={fadeInUp} className="flex items-start" data-id="element-451">
                    <CheckCircleIcon className="w-6 h-6 text-brand-red mr-3 flex-shrink-0 mt-0.5" data-id="element-452" />
                    <span className="text-charcoal font-semibold" data-id="element-453">
                      {service}
                    </span>
                  </motion.div>)}
              </div>
              <motion.div variants={fadeInUp} data-id="element-454">
                <Button variant="dark" onClick={() => setPage('services')} data-id="element-455">
                  View All Services
                </Button>
              </motion.div>
            </motion.div>
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeInUp} className="relative h-96 rounded-sm overflow-hidden shadow-xl" data-id="element-456">
              <img src="https://images.unsplash.com/photo-1513694203232-719a280e022f?q=80&w=1000&auto=format&fit=crop" alt={`Blasting services in ${locationData.city}`} className="w-full h-full object-cover" data-id="element-457" />
              <div className="absolute inset-0 bg-brand-red/10 mix-blend-multiply" data-id="element-458"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Us (Local Context) */}
      <section className="py-20 bg-steel-50" data-id="element-459">
        <div className="max-w-7xl mx-auto px-4 text-center" data-id="element-460">
          <motion.h2 initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-3xl md:text-5xl font-heading font-extrabold uppercase text-charcoal mb-12" data-id="element-461">
            Why {locationData.city} Chooses Us
          </motion.h2>
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-462">
            <motion.div variants={fadeInUp} className="bg-white p-8 border border-steel-200 shadow-sm" data-id="element-463">
              <TruckIcon className="w-12 h-12 text-brand-red mx-auto mb-6" data-id="element-464" />
              <h3 className="text-xl font-heading font-bold uppercase text-charcoal mb-3" data-id="element-465">
                Fully Mobile Setup
              </h3>
              <p className="text-steel-600 text-sm leading-relaxed" data-id="element-466">
                We bring our self-contained blasting rig directly to your{' '}
                {locationData.city} location. No need to transport heavy
                equipment or vehicles.
              </p>
            </motion.div>
            <motion.div variants={fadeInUp} className="bg-white p-8 border border-steel-200 shadow-sm" data-id="element-467">
              <ShieldIcon className="w-12 h-12 text-brand-red mx-auto mb-6" data-id="element-468" />
              <h3 className="text-xl font-heading font-bold uppercase text-charcoal mb-3" data-id="element-469">
                Urban-Friendly Process
              </h3>
              <p className="text-steel-600 text-sm leading-relaxed" data-id="element-470">
                Our dustless blasting suppresses 95% of airborne dust, making it
                safe and compliant for use in populated metro areas and active
                job sites.
              </p>
            </motion.div>
            <motion.div variants={fadeInUp} className="bg-white p-8 border border-steel-200 shadow-sm" data-id="element-471">
              <ZapIcon className="w-12 h-12 text-brand-red mx-auto mb-6" data-id="element-472" />
              <h3 className="text-xl font-heading font-bold uppercase text-charcoal mb-3" data-id="element-473">
                Fast Turnaround
              </h3>
              <p className="text-steel-600 text-sm leading-relaxed" data-id="element-474">
                We know downtime costs money. We provide fast estimates and
                efficient on-site service to keep your project moving forward.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-brand-red text-white text-center px-4" data-id="element-475">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInUp} className="max-w-3xl mx-auto" data-id="element-476">
          <h2 className="text-4xl md:text-5xl font-heading font-extrabold uppercase mb-6" data-id="element-477">
            Ready for Your Project in {locationData.city}?
          </h2>
          <p className="text-xl font-body mb-10 text-white/90" data-id="element-478">
            Contact us today for a free, no-obligation estimate for your surface
            preparation needs.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4" data-id="element-479">
            <Button variant="secondary" size="lg" onClick={() => setPage('contact')} showPhoneIcon className="w-full sm:w-auto border-none" data-id="element-480">
              Call (517) 777-8088
            </Button>
            <Button variant="outline-red" size="lg" onClick={() => setPage('contact')} className="w-full sm:w-auto" data-id="element-481">
              Request a Quote
            </Button>
          </div>
        </motion.div>
      </section>
    </div>;
}